import { CommandReceivedResult } from '@share/domain-objects/commands/command-received-result';
import { PeSignupStatus } from '../data-services/pe-signup-status';
import { PeSignupState } from '../dashboard/dashboard/pe-type.enum';
import {
  AdminOffice,
  Company,
  Domain,
  DynamicFormQuestion,
  EPNIntegration,
  Integration,
  MessageConfig,
  MessagingPhoneNumber,
  PeFeatureToggleDto,
  SelectedMessageConfigContent,
} from '@openapi';
import { create } from '@share/utils/create';
import { TimeBeforeTypeEnum } from '@share/domain-objects/company/integration/epn-integration/time-before-type.enum';
import { environment } from '../../environments/environment';
import {
  APPLICATION_NAMES,
  EPM_CLIENT_ID,
  EYEFINITY_CLIENT_ID,
  PeConnAccessToken,
} from '@share/constants';
import { Observable, of } from 'rxjs';
import { DropdownValueObject } from '@share/ui-components/dropdown/dropdown-value-object';
import {
  getMessagingConfigEnumAsNumber,
  MessagingConfigTypeEnum,
} from '@share/domain-objects/company/integration/epn-integration/messaging-config-type.enum';
import { ContentGroupTypeEnum } from '@share/domain-objects/messaging-configuration/content-group-type.enum';
import { MessageConfigContent } from '@share/domain-objects/messaging-configuration/message-config-content';
import RecallWeekOfEnum = EPNIntegration.RecallWeekOfEnum;
import RecallDayOfEnum = EPNIntegration.RecallDayOfEnum;

export function mockCommandReceivedResult(): CommandReceivedResult {
  return {
    success: true,
    errorMessage: 'Crap hit the fan',
    longRunning: false,
  } as CommandReceivedResult;
}

export function mockPeSignupStatus(): PeSignupStatus {
  const status = new PeSignupStatus();
  status.ewebextra = PeSignupState.SignUp;
  status.patientNotifications = PeSignupState.SignUp;
  return status;
}

export function mockCompanyData(): Company[] {
  return [
    {
      ...create<Company>(),
      companyName: "Bob's Company",
      practiceId: '12354',
      efan: '9000000000',
      alan: '9000000000',
      domains: [
        {
          ...create<Domain>(),
          efan: '9000000000',
          domainName: 'WeAreCool.com',
        },
      ],
      integration: {
        ...create<Integration>(),
        formsOn: false,
        schedulerOn: true,
        notificationEmailAddresses: ['bob@bob.COM'],
        epmIntegrated: true,
        adminOffices: [
          {
            ...create<AdminOffice>(),
            displayName: "bob's office 1",
            id: 1,
            timeZone: 2,
            phoneNumber: '5556667687',
          },
          {
            ...create<AdminOffice>(),
            displayName: 'hell hath no fury',
            id: 2,
            timeZone: 3,
          },
        ], // epm locations
        epnIntegration: {
          ...mockEpnIntegrationData(),
        },
        featureToggles: [...mockFeatureToggles([1, 2], 12345, true)],
        ewxIntegration: {
          portalUUID: '8b6dcb4720e846a0be13e2a287be41f6',
          schedulerUUID: '0ab962b05cfe405b95691fa81ce4fef4',
        },
      },
    } as Company,
  ];
}
export function mockPhoneNumber(id?: number): MessagingPhoneNumber {
  return {
    ...create<MessagingPhoneNumber>(),
    id: id ? id : 1,
    messagingPhoneNumber: '123456799',
    officePhoneNumber: '(916) 851-6969',
    active: true,
    messageConfigs: [
      {
        ...create<MessageConfig>(),
        epmLocationId: 1,
        type: MessagingConfigTypeEnum.REMINDERS,
        content:
          'Hi ${reminder.patientFirstName}, you have an appointment tomorrow. Come stop by and see us at ${reminder.practice.name}. ${crazyTimes}.',
        active: true,
        timeBefore: 3,
        hourToRun: '11',
      } as MessageConfig,
      {
        ...create<MessageConfig>(),
        epmLocationId: 1,
        type: MessagingConfigTypeEnum.DAY_OF_REMINDERS,
        active: true,
        timeBefore: 5,
        hourToRun: '12',
      } as MessageConfig,
      {
        ...create<MessageConfig>(),
        epmLocationId: 2,
        type: MessagingConfigTypeEnum.REMINDERS,
        active: true,
        timeBefore: 13,
        hourToRun: '13',
      } as MessageConfig,
    ],
  };
}

export function mockSelectedMessageConfigContent(
  contentIds: number[],
  epmLocationId = 123,
  messageConfigType = 0,
  practiceId = 123,
): SelectedMessageConfigContent[] {
  const content: SelectedMessageConfigContent[] = [];
  contentIds.forEach((id, index) => {
    content.push({
      id: index,
      messageConfigContentId: id,
      epmLocationId: epmLocationId,
      messageConfigTypeId: messageConfigType,
      practiceId: practiceId,
    } as SelectedMessageConfigContent);
  });
  return content;
}

export function mockEpnIntegrationData(): EPNIntegration {
  return {
    domainName: 'bob.com',
    remindersOn: false,
    recallsOn: true,
    dayOfRemindersOn: false,
    orderNotificationsOn: true,
    recallHourToRun: '12',
    recallWeekOf: RecallWeekOfEnum.Second,
    recallDayOf: RecallDayOfEnum.Monday,
    timeBeforeType: TimeBeforeTypeEnum.MONTHLY,
    selectedMessageConfigContent: [
      ...mockSelectedMessageConfigContent([1], 1, 0, 123),
      ...mockSelectedMessageConfigContent([3], 1, 3, 123),
      ...mockSelectedMessageConfigContent([7], 1, 4, 123),
      ...mockSelectedMessageConfigContent([9], null, 7, 123),
    ],
    messagingPhoneNumbers: [mockPhoneNumber(1), mockPhoneNumber(2)],
  } as EPNIntegration;
}
export function mockFeatureToggles(
  epmLocationIds: number[],
  practiceId: number,
  withCompanyWide = true,
): PeFeatureToggleDto[] {
  const toggles = [];
  for (let i = 0; i < epmLocationIds.length; i++) {
    let toggle = { ...create<PeFeatureToggleDto>() } as PeFeatureToggleDto;
    toggle.epmLocationId = epmLocationIds[i];
    toggle.activeScheduleTypeId = 0;
    toggle.toggleScheduleTypeId = 1;
    toggle.titleUserDescription = 'same day appointments #' + i;
    toggle.tooltipUserDescription = 'This is the tooltip #' + i;
    toggle.labelUserDescription = 'This is the label #' + i;
    toggle.possibleValues = '1 hour:1,2 hours:2,3 hours:3,4 hours:4';
    toggle.selectedValue = null;
    toggle.practiceId = practiceId;
    toggle.active = false;
    toggle.sequence = 1;
    toggles.push(toggle);

    toggle = { ...create<PeFeatureToggleDto>() } as PeFeatureToggleDto;
    toggle.epmLocationId = epmLocationIds[i];
    toggle.practiceId = practiceId;
    toggle.featureTypeId = 2;
    toggle.titleUserDescription = 'unmatched patient #' + i;
    toggle.tooltipUserDescription = 'This is the tooltip #' + i;
    toggle.labelUserDescription = 'This is the label #' + i;
    toggle.possibleValues = null;
    toggle.selectedValue = null;
    toggle.active = false;
    toggle.sequence = 2;
    toggles.push(toggle);
  }
  if (withCompanyWide) {
    const toggle = { ...create<PeFeatureToggleDto>() } as PeFeatureToggleDto;
    toggle.epmLocationId = null;
    toggle.activeScheduleTypeId = 6;
    toggle.toggleScheduleTypeId = 6;
    toggle.titleUserDescription = 'This is the title for company wide';
    toggle.tooltipUserDescription = 'This is the tooltip company wide';
    toggle.labelUserDescription = 'This is the label company wide';
    toggle.possibleValues = '1 hour:1,2 hours:2,3 hours:3,4 hours:4';
    toggle.selectedValue = null;
    toggle.practiceId = practiceId;
    toggle.active = false;
    toggle.sequence = 1;
    toggles.push(toggle);
  }
  return toggles;
}

export function mockPeConnAccessToken(): PeConnAccessToken {
  return {
    ...new PeConnAccessToken(),
    token_type: 'Bearer',
    access_token: mockAccessToken(),
    signupStatusEpn: PeSignupState.SignUp,
    signupStatusEwx: PeSignupState.SignUp,
    principal: 'trapper_eweb',
    companyId: '936',
    application:
      environment &&
      environment.mockDataBaseApplication === APPLICATION_NAMES.epm
        ? APPLICATION_NAMES.epm
        : APPLICATION_NAMES.eyefinity,
    efan: '9000000000',
    clientId:
      environment &&
      environment.mockDataBaseApplication === APPLICATION_NAMES.epm
        ? EPM_CLIENT_ID
        : EYEFINITY_CLIENT_ID,
    expires_in: 10000,
  };
}

export function mockMessageContentDropdownData$(): Observable<
  DropdownValueObject[]
> {
  const messageConfigContent = [];
  const data: DropdownValueObject[] = [];
  const shortMessage = 'Please this is s shorty';
  const longMessage =
    "This is super long, Because ${reminders.awesome} are the thing and we'll get rid of this code someday.";
  let getDataFromApi = mockMessageConfigContent(
    MessagingConfigTypeEnum.REMINDERS,
    ContentGroupTypeEnum.SIMPLE,
  );
  getDataFromApi.id = 1;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 2;
  getDataFromApi.content = getDataFromApi.content + ' ' + longMessage;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 3;
  getDataFromApi.content = shortMessage;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.content = 'This is the real message that should load up';
  getDataFromApi.id = 11;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  // greeting

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 4;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.GREETING;
  getDataFromApi.content = 'Hello ${reminder.patientFirstName}';
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.GREETING;
  getDataFromApi.content = 'Hi ${reminder.patientFirstName}';
  getDataFromApi.id = 5;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  // purpose

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 6;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.PURPOSE;
  getDataFromApi.content =
    'you have an eye appointment with ${reminder.providerLastName}$';
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.content =
    'you are seeing ${reminder.providerLastName}$ at $reminder.practice.name}';
  getDataFromApi.id = 7;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.PURPOSE;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  // when
  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 8;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.WHEN;
  getDataFromApi.content = 'at ${startTime} on ${appointmentDate}.';
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.content = 'on ${appointmentDate} at ${startTime}.';
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.WHEN;
  getDataFromApi.id = 9;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  // optional
  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.id = 10;
  getDataFromApi.content = 'Please wear a mask.';
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.OPTIONAL;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  getDataFromApi = { ...getDataFromApi };
  getDataFromApi.content = 'We are located at $ADDRESS$.';
  getDataFromApi.id = 12;
  getDataFromApi.contentGroupTypeId = ContentGroupTypeEnum.OPTIONAL;
  data.push({ label: getDataFromApi.content, value: getDataFromApi });
  messageConfigContent.push(getDataFromApi);

  return of(data);
}

export function mockMessageConfigContent(
  configTypeId: MessagingConfigTypeEnum,
  contentTypeId: ContentGroupTypeEnum,
): MessageConfigContent {
  const content = new MessageConfigContent();
  content.content =
    'Hi there buddy, howzit going? You have an eyeball appointment today with Dr Bob at 3 pm on 10/22/20.  We really hope you show up and please wear a mask.';
  content.messageConfigTypeId = getMessagingConfigEnumAsNumber(configTypeId);
  content.contentGroupTypeId = contentTypeId;
  content.id = 1;
  return content;
}

export function mockAccessToken(): string {
  return 'thisisamockaccesstoken1234567890';
}

export const mockEditDialogQuestions: DynamicFormQuestion[] = [
  {
    id: 'patient-acknowledgement-question-02',
    sequence: 3,
    groupId: 'acknowledgement-information-group',
    placeHolder: 'HIPAA Compliance Symbol',
    selectedValue:
      'https://mt-prd-pp-patient-portal.s3.us-west-2.amazonaws.com/acknowledgment-forms/hipaa-images-sml/Hipaa_1.jpg',
    elementType: 'IMAGE',
    width: 100,
  },
  {
    id: 'patient-acknowledgement-question-02-1',
    sequence: 3,
    groupId: 'acknowledgement-information-group',
    placeHolder: 'HIPAA Compliance Symbol second',
    selectedValue: '',
    elementType: 'IMAGE',
    width: 100,
    potentialAnswers: [
      {
        label: 'HIPAA 01',
        value:
          'https://mt-prd-pp-patient-portal.s3.us-west-2.amazonaws.com/acknowledgment-forms/hipaa-images-sml/Hipaa_1.jpg',
      },
      {
        label: 'HIPAA 02',
        value:
          'https://mt-prd-pp-patient-portal.s3.us-west-2.amazonaws.com/acknowledgment-forms/hipaa-images-sml/Hipaa_2.jpg',
      },
      {
        label: 'HIPAA 03',
        value:
          'https://mt-prd-pp-patient-portal.s3.us-west-2.amazonaws.com/acknowledgment-forms/hipaa-images-sml/Hipaa_3.jpg',
      },
      {
        label: 'HIPAA 04',
        value:
          'https://mt-prd-pp-patient-portal.s3.us-west-2.amazonaws.com/acknowledgment-forms/hipaa-images-sml/Hipaa_4.jpg',
      },
      {
        label: 'HIPAA 05',
        value:
          'https://mt-prd-pp-patient-portal.s3.us-west-2.amazonaws.com/acknowledgment-forms/hipaa-images-sml/Hipaa_5.jpg',
      },
    ],
  },
  {
    id: 'patient-acknowledgement-question-03',
    sequence: 4,
    groupId: 'acknowledgement-information-group',
    placeHolder: 'View Patient Rights',
    selectedValue: '',
    elementType: 'PDF',
    width: 100,
    show: false,
  },
  {
    id: 'signature-question-01',
    width: 100,
    groupId: 'signature-group',
    required: true,
    sequence: 1,
    elementType: DynamicFormQuestion.ElementTypeEnum.Alpha,
    placeHolder: 'Full Name',
    patientPrompt:
      "By typing my name and clicking 'Complete' button below, I have voluntarily answered all the questions truthfully and to the best of my ability.",
    integrationValue: [{ target: 'OtherQuestions.SectionQuestions' }],
  },
  {
    id: 'patient-information-question-06',
    width: 30,
    groupId: 'patient-information-group',
    required: false,
    sequence: 1,
    elementType: DynamicFormQuestion.ElementTypeEnum.Dropdown,
    placeHolder: 'Birth Sex',
    constraintOptions: [],
    integrationValue: [{ target: 'Demographic.BirthSex' }],
    potentialAnswers: [
      { label: 'Female', value: 'F', show: true },
      { label: 'Male', value: 'M', show: true },
    ],
  },
  {
    id: 'patient-information-question-01',
    width: 30,
    groupId: 'patient-information-group',
    required: true,
    sequence: 1,
    elementType: DynamicFormQuestion.ElementTypeEnum.Alpha,
    placeHolder: 'First Name (Legal)',
    characterCount: 30,
    constraintOptions: [
      { action: 'PREPOPULATE', data: 'PatientPrepopulationData.firstName' },
    ],
    integrationValue: [{ target: 'Demographic.FirstName' }],
  },
  {
    id: 'patient-information-question-05',
    width: 30,
    groupId: 'patient-information-group',
    required: true,
    sequence: 1,
    elementType: DynamicFormQuestion.ElementTypeEnum.Date,
    placeHolder: 'Date of Birth',
    constraintOptions: [
      { action: 'PREPOPULATE', data: 'PatientPrepopulationData.dob' },
    ],
    integrationValue: [{ target: 'Demographic.Dob' }],
  },
  {
    id: 'contact-information-question-09',
    width: 50,
    groupId: 'contact-information-group',
    required: true,
    sequence: 1,
    elementType: DynamicFormQuestion.ElementTypeEnum.Email,
    placeHolder: 'Email Address',
    constraintOptions: [],
    integrationValue: [{ target: 'Communication.Email' }],
  },
  {
    id: 'contact-information-question-01',
    width: 75,
    groupId: 'contact-information-group',
    required: true,
    sequence: 1,
    elementType: DynamicFormQuestion.ElementTypeEnum.Input,
    placeHolder: 'Address',
    constraintOptions: [],
    integrationValue: [{ target: 'Address.Addresses.Address' }],
  },
  {
    id: 'patient-information-question-08',
    width: 15,
    groupId: 'patient-information-group',
    required: false,
    sequence: 1,
    elementType: DynamicFormQuestion.ElementTypeEnum.Numeric,
    placeHolder: 'Last 4 SSN',
    characterCount: 4,
    constraintOptions: [],
    integrationValue: [{ target: 'Demographic.SsnLastFour' }],
  },
  {
    id: 'patient-acknowledgement-question-00',
    sequence: 1,
    groupId: 'acknowledgement-information-group',
    patientPrompt: 'Notice of Privacy Practices',
    elementType: 'TITLE',
    required: true,
    width: 100,
  },
  {
    id: 'patient-acknowledgement-question-01',
    sequence: 2,
    groupId: 'acknowledgement-information-group',
    patientPrompt:
      'This notice describes how health information about you may be used and disclosed and how you can get access to this information. Please review it carefully.',
    elementType: 'TEXT',
    width: 100,
  },
];

export const mockCheckboxesQuestions: DynamicFormQuestion[] = [
  {
    id: 'contact-information-question-10',
    show: false,
    width: 75,
    groupId: 'contact-information-group',
    required: true,
    sequence: 1,
    elementType: 'CHECKBOX',
    placeHolder: 'Primary Address',
    selectedValue: 'IsPrimary',
    integrationValue: [
      {
        target: 'Address.Addresses',
        collectionIndex: 0,
      },
    ],
    potentialAnswers: [
      {
        label: 'Primary Address',
        value: 'IsPrimary',
        show: true,
      },
    ],
  },
  {
    id: 'insured-party-question-07',
    show: false,
    width: 100,
    groupId: 'insured-party-group',
    required: false,
    sequence: 1,
    elementType: 'CHECKBOX',
    placeHolder: '',
    constraintId: 'insurance-information-question-04',
    selectedValue: '',
    potentialAnswers: [
      {
        label: 'Same Address as Patient',
        value: 'sameAddressAsPatient',
        show: true,
      },
    ],
    constraintOptions: [
      {
        constraintId: 'insurance-information-question-04',
        action: 'HIDE',
        trigger: 'Self',
      },
      {
        constraintId: 'insurance-information-question-04',
        action: 'SHOW',
        trigger: 'Spouse',
      },
      {
        constraintId: 'insurance-information-question-04',
        action: 'SHOW',
        trigger: 'Child',
      },
      {
        constraintId: 'insurance-information-question-04',
        action: 'SHOW',
        trigger: 'Other',
      },
    ],
  },
  {
    id: 'second-insurance-party-question-08',
    show: false,
    width: 100,
    groupId: 'second-insurance-party-group',
    required: false,
    sequence: 8,
    elementType: 'CHECKBOX',
    placeHolder: '',
    constraintId: 'second-insurance-question-04',
    potentialAnswers: [
      {
        label: 'Same Address as Patient',
        value: 'sameAddressAsPatient',
        show: true,
      },
    ],
    constraintOptions: [
      {
        constraintId: 'second-insurance-question-04',
        action: 'HIDE',
        trigger: 'Self',
      },
      {
        constraintId: 'second-insurance-question-04',
        action: 'SHOW',
        trigger: 'Spouse',
      },
      {
        constraintId: 'second-insurance-question-04',
        action: 'SHOW',
        trigger: 'Child',
      },
      {
        constraintId: 'second-insurance-question-04',
        action: 'SHOW',
        trigger: 'Other',
      },
      {
        constraintId: 'second-insurance-question-04',
        action: 'HIDE',
        trigger: 'TRIGGER_EMPTY_VALUE',
      },
    ],
  },
  {
    id: 'eye-related-question-02',
    width: 100,
    groupId: 'eye-related-group',
    required: false,
    sequence: 1,
    elementType: 'CHECKBOX',
    patientPrompt: 'Are you experiencing any of the following?',
    integrationValue: [
      {
        target: 'OcularHistory.History',
        categoryId: 4,
        formSystemId: 1,
      },
    ],
    potentialAnswers: [
      {
        label: 'None',
        value: 'None',
        show: true,
      },
      {
        label: 'Itchy Eyes',
        value: 'Itchy Eyes',
        show: true,
      },
      {
        label: 'Blurred Distance Vision',
        value: 'Blurred Distance Vision',
        show: false,
      },
      {
        label: 'Loss of Vision',
        value: 'Loss of Vision',
        show: true,
      },
      {
        label: 'Blurred Near Vision',
        value: 'Blurred Near Vision',
        show: false,
      },
      {
        label: 'Night Vision Problems',
        value: 'Night Vision Problems',
        show: true,
      },
      {
        label: 'Double Vision',
        value: 'Double Vision',
        show: false,
      },
      {
        label: 'Objects Floating in Vision',
        value: 'Objects Floating in Vision',
        show: true,
      },
      {
        label: 'Dry Eyes',
        value: 'Dry Eyes',
        show: false,
      },
      {
        label: 'Pain in Eyes',
        value: 'Pain in Eyes',
        show: true,
      },
      {
        label: 'Eye Strain',
        value: 'Eye Strain',
        show: false,
      },
      {
        label: 'Red Eyes',
        value: 'Red Eyes',
        show: true,
      },
      {
        label: 'Flashes of Light',
        value: 'Flashes of Light',
        show: false,
      },
      {
        label: 'Sensitivity to Light',
        value: 'Sensitivity to Light',
        show: true,
      },
      {
        label: 'Gritty Feeling Eyes',
        value: 'Gritty Feeling Eyes',
        show: false,
      },
      {
        label: 'Watery Eyes',
        value: 'Watery Eyes',
        show: true,
      },
      {
        label: 'Headaches',
        value: 'Headaches',
        show: false,
      },
    ],
    potentialAnswersWidth: 50,
  },
  {
    id: 'eye-surgery-question-01',
    width: 100,
    groupId: 'eye-surgery-group',
    required: false,
    sequence: 1,
    elementType: 'CHECKBOX',
    patientPrompt: 'Have you had eye surgery for any of the follow?',
    integrationValue: [
      {
        target: 'OcularSurgeries.Surgeries',
        categoryId: 7,
        formSystemId: 1,
      },
    ],
    potentialAnswers: [
      {
        label: 'Glaucoma Surgery',
        value: 'Glaucoma Surgery',
        show: false,
      },
      {
        label: 'Refractive (Lasik)',
        value: 'Refractive (Lasik)',
        show: true,
      },
      {
        label: 'Cataract Surgery',
        value: 'Cataract Surgery',
        show: true,
      },
      {
        label: 'Retinal Detachment',
        value: 'Retinal Detachment',
        show: true,
      },
    ],
    potentialAnswersWidth: 50,
  },
];

export const mockMultiCheckboxesQuestions: DynamicFormQuestion[] = [
  {
    id: 'medical-history-question-01',
    width: 100,
    groupId: 'medical-history-group',
    required: false,
    sequence: 1,
    elementType: 'MULTI_CHECKBOX',
    placeHolder: 'Me,Immediate Family',
    integrationValue: [
      {
        target: 'MedicalHistory.History',
        categoryId: 2,
        columnName: 'Me',
        formSystemId: 1,
      },
      {
        target: 'FamilyHistory.History',
        categoryId: 1,
        columnName: 'Immediate Family',
        formSystemId: 1,
        fixedLookupValue: 'Other',
      },
    ],
    potentialAnswers: [
      {
        label: 'None',
        value: 'None',
        show: false,
      },
      {
        label: 'AIDS/HIV',
        value: 'AIDS/HIV',
        show: true,
      },
      {
        label: 'Allergy',
        value: 'Allergy',
        show: false,
      },
      {
        label: 'Arthritis',
        value: 'Arthritis',
        show: true,
      },
      {
        label: 'Blood',
        value: 'Blood',
        show: false,
      },
      {
        label: 'Cancer',
        value: 'Cancer',
        show: true,
      },
      {
        label: 'Cardiovascular',
        value: 'Cardiovascular',
        show: true,
      },
      {
        label: 'Diabetes',
        value: 'Diabetes',
        show: true,
      },
      {
        label: 'Ears, Nose, Throat, Mouth',
        value: 'Ears\\, Nose\\, Throat\\, Mouth',
        show: false,
      },
      {
        label: 'Endocrine',
        value: 'Endocrine',
        show: true,
      },
      {
        label: 'Gastrointestinal',
        value: 'Gastrointestinal',
        show: false,
      },
      {
        label: 'Heart Disease',
        value: 'Heart Disease',
        show: true,
      },
      {
        label: 'High Blood Pressure',
        value: 'High Blood Pressure',
        show: false,
      },
      {
        label: 'Immune Disorder',
        value: 'Immune Disorder',
        show: true,
      },
      {
        label: 'Kidney Disease',
        value: 'Kidney Disease',
        show: false,
      },
      {
        label: 'Lymphatic',
        value: 'Lymphatic',
        show: true,
      },
      {
        label: 'Neurological',
        value: 'Neurological',
        show: false,
      },
      {
        label: 'Psychiatric',
        value: 'Psychiatric',
        show: true,
      },
      {
        label: 'Skin Disorder',
        value: 'Skin Disorder',
        show: false,
      },
      {
        label: 'Thyroid Disease',
        value: 'Thyroid Disease',
        show: true,
      },
    ],
    potentialAnswersWidth: 100,
  },
  {
    id: 'eye-related-question-01',
    width: 100,
    groupId: 'eye-related-group',
    required: false,
    sequence: 1,
    elementType: 'MULTI_CHECKBOX',
    placeHolder: 'Me,Immediate Family',
    patientPrompt:
      'Have you or your immediate family ever been treated for any of the following?',
    integrationValue: [
      {
        target: 'OcularHistory.History',
        categoryId: 4,
        columnName: 'Me',
        formSystemId: 1,
      },
      {
        target: 'FamilyHistory.History',
        categoryId: 1,
        columnName: 'Immediate Family',
        formSystemId: 1,
        fixedLookupValue: 'Other',
      },
    ],
    potentialAnswers: [
      {
        label: 'None',
        value: 'None',
        show: true,
      },
      {
        label: 'Blindness',
        value: 'Blindness',
        show: true,
      },
      {
        label: 'Cataract',
        value: 'Cataract',
        show: false,
      },
      {
        label: 'Glaucoma',
        value: 'Glaucoma',
        show: true,
      },
      {
        label: 'Macular Degeneration',
        value: 'Macular Degeneration',
        show: false,
      },
      {
        label: 'Retinal Detachment',
        value: 'Retinal Detachment',
        show: true,
      },
    ],
    potentialAnswersWidth: 50,
  },
  {
    id: 'tobacco-etc-question-04',
    width: 100,
    groupId: 'tobacco-etc-group',
    required: false,
    sequence: 4,
    elementType: 'CHECKBOX',
    patientPrompt: 'Are you currently?',
    integrationValue: [
      {
        target: 'MedicalHistory.History',
        categoryId: 2,
      },
    ],
    potentialAnswers: [
      {
        label: 'Pregnant',
        value: 'Pregnant',
        show: true,
      },
      {
        label: 'Nursing',
        value: 'Nursing',
        show: false,
      },
    ],
    potentialAnswersWidth: 0,
  },
];

export const mockRadioQuestions: DynamicFormQuestion[] = [
  {
    id: 'patient-acknowledgement-question-05',
    sequence: 6,
    groupId: 'acknowledgement-information-group',
    elementType: 'RADIO',
    patientPrompt:
      'This test is quick, painless, and does not require dilation.  There are no side effects. There is an out-of-pocket fee for the test and can be covered by HSA/FSA.',
    required: false,
    potentialAnswers: [
      {
        label: 'Yes, I want this test during my visit',
        value: 'Yes, I want this test during my visit',
        show: true,
      },
      {
        label: 'I would like to discuss it with my doctor',
        value: 'I would like to discuss it with my doctor',
        show: true,
      },
    ],
    width: 100,
  },
  {
    id: 'medication-allergy-question-02',
    width: 51,
    groupId: 'medication-allergy-group',
    required: false,
    sequence: 1,
    elementType: 'RADIO',
    placeHolder: 'Allergies',
    patientPrompt: 'Are you allergic to any medications?',
    potentialAnswers: [
      {
        label: 'Yes',
        value: 'Yes',
      },
      {
        label: 'No',
        value: 'No',
      },
    ],
    potentialAnswersWidth: 0,
  },
  {
    id: 'prompt-secondary-insurance-question-01',
    width: 50,
    groupId: 'prompt-secondary-insurance-group',
    required: true,
    sequence: 1,
    elementType: 'RADIO',
    patientPrompt: 'Do you have a second insurance?',
    selectedValue: 'No',
    potentialAnswers: [
      {
        label: 'Yes',
        value: 'Yes',
      },
      {
        label: 'No',
        value: 'No',
      },
    ],
    potentialAnswersWidth: 25,
  },
  {
    id: 'tobacco-etc-question-01',
    width: 100,
    groupId: 'tobacco-etc-group',
    required: false,
    sequence: 1,
    elementType: 'RADIO',
    patientPrompt: 'Do you use tobacco products?',
    integrationValue: [
      {
        target: 'OtherQuestions.SectionQuestions',
      },
    ],
    potentialAnswers: [
      {
        label: 'Non-smoker',
        value: 'Non-smoker',
      },
      {
        label: 'Current smoker',
        value: 'Current smoker',
      },
      {
        label: 'Former smoker',
        value: 'Former smoker',
      },
    ],
    potentialAnswersWidth: 50,
  },
  {
    id: 'tobacco-etc-question-02',
    width: 100,
    groupId: 'tobacco-etc-group',
    required: false,
    sequence: 2,
    elementType: 'RADIO',
    patientPrompt: 'Do you drink alcohol?',
    integrationValue: [
      {
        target: 'OtherQuestions.SectionQuestions',
      },
    ],
    potentialAnswers: [
      {
        label: 'None',
        value: 'None',
      },
      {
        label: 'Less than 1 a day',
        value: 'Less than 1 a day',
        show: true,
      },
      {
        label: '1 to 2 a day',
        value: '1 to 2 a day',
      },
      {
        label: '3 or more a day',
        value: '3 or more a day',
      },
    ],
    potentialAnswersWidth: 50,
  },
  {
    id: 'tobacco-etc-question-03',
    width: 100,
    groupId: 'tobacco-etc-group',
    required: false,
    sequence: 3,
    elementType: 'RADIO',
    patientPrompt: 'Do you use other substances?',
    integrationValue: [
      {
        target: 'OtherQuestions.SectionQuestions',
      },
    ],
    potentialAnswers: [
      {
        label: 'Yes',
        value: 'Yes',
      },
      {
        label: 'No',
        value: 'No',
      },
    ],
    potentialAnswersWidth: 0,
  },
];
